import { useMemo, useState } from 'react';
import { Alert, Box, InputLabel, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { useFlags } from '@rideiq/contexts/LDContext';
import { Button } from '@rideiq/shared/components/Button';
import { useQuery } from '@tanstack/react-query';

import { getAssociatedLocationDetails } from '../../../../api/services/Riders';
import { RiderLocation } from '../Location/Location.types';
import { RiderLocationForm } from '../RiderLocationForm';
import { RiderLocationFormSkeleton } from '../RiderLocationForm/RiderLocationFormSkeleton';

import { CloseButton, FieldWithLabel } from './EditLocationModal.styles';

const withPreservedStagedReplaceLocation = (fetchedLocation: RiderLocation, stagedLocation: RiderLocation) => ({
  ...fetchedLocation,
  replaceLocation: stagedLocation?.replaceLocation ?? fetchedLocation.replaceLocation,
});

export const EditLocationModal = ({
  isOpen,
  riderSlug,
  location,
  associatedLocations,
  isLoading,
  isNew = false,
  isDirty = false,
  hideSavedLocationsInFindAndReplace = false,
  onCancel,
  onDelete,
  onSubmit,
}) => {
  const { rideiqMbmAuthorizedGroupsRedesignEnabled } = useFlags();
  const [isFormLoading, setIsFormLoading] = useState(false);
  const detailsQuery = useEditLocationDetails(riderSlug, location, isNew || isDirty);
  const locationDetails = (!isNew && !isDirty && detailsQuery.data) || location;
  const isDetailsLoading = rideiqMbmAuthorizedGroupsRedesignEnabled && detailsQuery.isLoading;
  const hasDetailsError = rideiqMbmAuthorizedGroupsRedesignEnabled && detailsQuery.isError;

  const initialValues = useMemo(
    () => ({
      primary: locationDetails?.primary,
      mustBeMet: locationDetails?.mustBeMet || false,
      locationId: locationDetails?.id,
      mustBeMetProtocol: {
        authorizedContactGroups:
          locationDetails?.authorizedContactGroups?.map((group) => {
            return { ...group, saved: true };
          }) || [],
        locationProtocol: locationDetails?.locationProtocol?.value || '',
      },
      replaceLocation: locationDetails?.replaceLocation,
    }),
    [locationDetails]
  );

  return (
    <Modal
      open={isOpen}
      data-testid="rider-location-edit-modal"
      maxWidth={'xl'}
      PaperProps={{ style: { overflowY: 'hidden' } }}
    >
      <Modal.Title>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {"Edit Rider's Location"}
          <CloseButton onClick={onCancel} data-testid="closeButton" aria-label="Close rider add location modal">
            <Svg height="20px" icon="close-outlined" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <FieldWithLabel>
          <InputLabel htmlFor="address">Address</InputLabel>
          <Text>{locationDetails.address}</Text>
        </FieldWithLabel>
        {locationDetails.name && (
          <FieldWithLabel>
            <InputLabel htmlFor="locationName">Location name</InputLabel>
            <Text>{locationDetails.name}</Text>
          </FieldWithLabel>
        )}

        {hasDetailsError && (
          <Box>
            <Alert severity="error" message="Unable to load this location. Please try again before saving." />
            <Button color="tertiary" onClick={() => detailsQuery.refetch()}>
              Try again
            </Button>
          </Box>
        )}
        {isDetailsLoading && <RiderLocationFormSkeleton location={location} />}
        {!hasDetailsError && !isDetailsLoading && (
          <RiderLocationForm
            onLoadingChange={setIsFormLoading}
            isEditView
            location={locationDetails}
            initialValues={initialValues}
            associatedLocations={associatedLocations}
            riderSlug={riderSlug}
            onSubmit={onSubmit}
            onCancel={onCancel}
            hideSavedLocationsInFindAndReplace={hideSavedLocationsInFindAndReplace}
          />
        )}
      </Modal.Content>
      <Modal.Actions style={{ justifyContent: 'space-between' }}>
        <Button color="destructive" onClick={onDelete}>
          Remove from rider
        </Button>
        <Box display="flex" gap="1rem">
          <Button color="tertiary" loading={isLoading} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            loading={isLoading}
            disabled={isDetailsLoading || hasDetailsError || isFormLoading}
            form={'rider-associated-location'}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Modal.Actions>
    </Modal>
  );
};

const useEditLocationDetails = (riderSlug: string, location: RiderLocation, isDraft: boolean) =>
  useQuery({
    queryKey: ['editRiderLocation', riderSlug, location.id],
    enabled: !!riderSlug && !isDraft,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    queryFn: async () => {
      const response = await getAssociatedLocationDetails(riderSlug, location.id || 0);

      if (response?.type !== 'success') {
        throw new Error('Unable to load rider location details');
      }

      return withPreservedStagedReplaceLocation(response.data.location, location);
    },
  });
